import React, { FC, useMemo } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PlaneIcon, ShipIcon, TrainIcon, TruckIcon, WaterIcon } from "../../../../../../assets/icons";
import { formatNumber } from "../../../../../../common/utils";
import { getNodeTypeIcon } from "../../../../../../constants/nodeTypeIcons";
import { CarbonSnapResultsResponse } from "../../../../../../datasources/carbonsnap/io/response";
import { TransportArc, TransportMode, TransportNode } from "../../../../../../datasources/carbonsnap/models";

interface Props {
	arcDetails: CarbonSnapResultsResponse["arcDetails"];
	route: CarbonSnapResultsResponse["route"];
}

const ArcTab: FC<Props> = ({ arcDetails, route }) => {
	const { t } = useTranslation("feature.carbonsnap");

	const formatValue = (value: number, measure: string): string => {
		return `${formatNumber(value, "decimal", 2, 2)} ${measure}`;
	};

	const formatIntensity = (value: number, measure: string): string => {
		return `${formatNumber(value, "decimal", 4, 4)} ${measure}`;
	};

	// Helper per trovare i dettagli di un nodo dal nome
	const findNodeDetails = (nodeName: string): TransportNode | undefined => {
		return route.nodes.find((node) => {
			// Cerca per nome o indirizzo completo
			const fullAddress = formatNodeAddress(node);
			return node.name === nodeName || node.address === nodeName || fullAddress === nodeName;
		});
	};

	// Helper per trovare i dettagli di un arco dall'ID
	const findArcDetails = (arcId: string): TransportArc | undefined => {
		return route.arcs.find((arc) => arc.id === arcId);
	};

	// Helper per formattare l'indirizzo completo del nodo
	const formatNodeAddress = (node: TransportNode): string => {
		const parts: string[] = [];
		if (node.address) parts.push(node.address);
		if (node.zipCode || node.city) {
			const cityPart = [node.zipCode, node.city].filter(Boolean).join(" ");
			if (cityPart) parts.push(cityPart);
		}
		if (node.province && parts.length > 0) {
			parts[parts.length - 1] += ` ${node.province}`;
		}
		return parts.join(", ");
	};

	// Utility per ottenere l'icona del veicolo basata su transportMode
	const getTransportModeIcon = (mode: TransportMode | null | undefined): React.ElementType => {
		switch (mode) {
			case TransportMode.Road:
				return TruckIcon;
			case TransportMode.Train:
				return TrainIcon;
			case TransportMode.Sea:
				return ShipIcon;
			case TransportMode.Air:
				return PlaneIcon;
			case TransportMode.Water:
				return WaterIcon;
			default:
				return TruckIcon;
		}
	};

	// NodeCard component (con icona interna)
	const NodeCard: FC<{
		nodeName: string;
		hubEmissions?: { value: number; measure: string };
	}> = ({ nodeName, hubEmissions }) => {
		const nodeDetails = findNodeDetails(nodeName);
		const fullAddress = nodeDetails ? formatNodeAddress(nodeDetails) : nodeName;
		const NodeIcon = nodeDetails?.nodeTypeId ? getNodeTypeIcon(nodeDetails.nodeTypeId) : LocationOnIcon;

		return (
			<Paper sx={{ p: 2 }}>
				<Stack direction="row" spacing={2} sx={{ flex: 1 }}>
					{/* Icona circolare dentro NodeCard */}
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							pt: 0.5,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 40,
								height: 40,
								borderRadius: "50%",
								bgcolor: "grey.200",
							}}
						>
							{NodeIcon && <NodeIcon fontSize="small" color="primary" />}
						</Box>
					</Box>
					<Box sx={{ flex: 1 }}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
							<Box>
								{nodeDetails?.nodeTypeName && (
									<Typography variant="h6" sx={{ mb: 0.5 }}>
										{nodeDetails.nodeTypeName}
									</Typography>
								)}
								{nodeDetails?.code && (
									<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
										{nodeDetails.code}
									</Typography>
								)}
							</Box>
							<Button
								variant="text"
								size="small"
								startIcon={<MapIcon />}
								onClick={() => console.log("View on map:", nodeName)}
							>
								{t("results/details/arc/view-on-map") || "VEDI SU MAPPA"}
							</Button>
						</Stack>
						{fullAddress && (
							<Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
								<LocationOnIcon fontSize="small" color="action" />
								<Typography variant="body2" color="text.secondary">
									{fullAddress}
								</Typography>
							</Stack>
						)}
						{hubEmissions && (
							<Stack direction="row" spacing={0.5} alignItems="center">
								<Typography variant="body2" color="text.secondary">
									{t("results/details/arc/hub emissions") || "Emissioni hub"}:
								</Typography>
								<Typography variant="body2" fontWeight="medium">
									{formatValue(hubEmissions.value, hubEmissions.measure)}
								</Typography>
							</Stack>
						)}
					</Box>
				</Stack>
			</Paper>
		);
	};

	// ArcCard component (senza icona interna)
	const ArcCard: FC<{
		arc: CarbonSnapResultsResponse["arcDetails"][0];
	}> = ({ arc }) => {
		const arcDetails = findArcDetails(arc.arcId);

		// Costruisci descrizione veicolo dettagliata
		const vehicleDescription = useMemo(() => {
			if (!arcDetails) {
				return `${t("results/details/arc/transport") || "Trasporto"} ${arc.fromNode} → ${arc.toNode}`;
			}

			const parts: string[] = [];

			// Aggiungi tipo veicolo se disponibile (es. "Motrice")
			// Per ora usiamo il transportMode come base
			const modeLabels: Record<TransportMode, string> = {
				[TransportMode.Road]: t("arc/controls/mode/road") || "Strada",
				[TransportMode.Train]: t("arc/controls/mode/train") || "Treno",
				[TransportMode.Sea]: t("arc/controls/mode/sea") || "Mare",
				[TransportMode.Air]: t("arc/controls/mode/air") || "Aereo",
				[TransportMode.Water]: t("arc/controls/mode/inland waterway") || "Acqua",
			};

			const modeLabel = modeLabels[arcDetails.transportMode] || arcDetails.transportMode;
			parts.push(modeLabel);

			// Aggiungi payload massimo se disponibile
			if (arcDetails.weight && arcDetails.weightUnit) {
				const weightUnitLabel = arcDetails.weightUnit === "tonne" ? "t" : arcDetails.weightUnit;
				parts.push(`(Pt max: ${arcDetails.weight}${weightUnitLabel})`);
			}

			// Aggiungi tipo motore se disponibile (es. "Elettrico")
			// TODO: Aggiungere quando disponibile nella risposta API

			return parts.join(" ");
		}, [arcDetails, arc.fromNode, arc.toNode, t]);

		// Formatta peso trasportato
		const cargoWeight =
			arcDetails?.weight && arcDetails?.weightUnit
				? `${formatNumber(arcDetails.weight, "decimal", 2, 2)} ${arcDetails.weightUnit === "tonne" ? "t" : arcDetails.weightUnit}`
				: null;

		return (
			<Paper sx={{ p: 2 }}>
				<Box sx={{ flex: 1 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="h6">{vehicleDescription}</Typography>
						<Button
							variant="text"
							size="small"
							startIcon={<MapIcon />}
							onClick={() => console.log("View on map:", arc.arcId)}
						>
							{t("results/details/arc/view-on-map") || "VEDI SU MAPPA"}
						</Button>
					</Stack>
					<Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t("results/details/arc/distance") || "Distanza"}
							</Typography>
							<Typography variant="body1" fontWeight="medium">
								{formatValue(arc.distance.value, arc.distance.measure)}
							</Typography>
						</Box>
						{cargoWeight && (
							<Box>
								<Typography variant="caption" color="text.secondary">
									{t("results/details/arc/cargo-weight") || "Peso trasportato"}
								</Typography>
								<Typography variant="body1" fontWeight="medium">
									{cargoWeight}
								</Typography>
							</Box>
						)}
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t("results/details/arc/transport emissions") || "Emissioni totali"}
							</Typography>
							<Typography variant="body1" fontWeight="medium">
								{formatValue(arc.transportEmissions.value, arc.transportEmissions.measure)}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="text.secondary">
								{t("results/details/arc/emission intensity") || "Emission intensity"}
							</Typography>
							<Typography variant="body1" fontWeight="medium">
								{formatIntensity(arc.emissionIntensity.value, arc.emissionIntensity.measure)}
							</Typography>
						</Box>
						{/* Particulate - da aggiungere quando disponibile nella risposta API */}
					</Stack>
				</Box>
			</Paper>
		);
	};

	// Build sequence of nodes and arcs
	type SequenceItem =
		| { type: "node"; nodeName: string; hubEmissions?: { value: number; measure: string } }
		| { type: "arc"; arc: CarbonSnapResultsResponse["arcDetails"][0] };

	const sequence: SequenceItem[] = [];

	arcDetails.forEach((arc) => {
		// Add fromNode
		sequence.push({
			type: "node",
			nodeName: arc.fromNode,
			hubEmissions: arc.hubEmissions,
		});

		// Add arc
		sequence.push({ type: "arc", arc });
	});

	// Add last toNode
	if (arcDetails.length > 0) {
		const lastArc = arcDetails[arcDetails.length - 1];
		sequence.push({
			type: "node",
			nodeName: lastArc.toNode,
		});
	}

	if (arcDetails.length === 0) {
		return (
			<Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
				{t("results/details/arc/no-data") || "Nessun arco disponibile"}
			</Typography>
		);
	}

	return (
		<Stack direction="column" spacing={2}>
			{sequence.map((item, index) => {
				if (item.type === "node") {
					// NODO: riga singola a larghezza piena
					return (
						<NodeCard
							key={`node-${item.nodeName}-${index}`}
							nodeName={item.nodeName}
							hubEmissions={item.hubEmissions}
						/>
					);
				} else {
					// ARCO: riga con due colonne (icona + linea a sinistra, card a destra)
					const arcDetails = findArcDetails(item.arc.arcId);
					const IconComponent = getTransportModeIcon(arcDetails?.transportMode);

					// Determina se questo arco è il primo o l'ultimo per estendere la linea
					const isFirstArc = index === 0 || sequence[index - 1]?.type === "node";
					const isLastArc = index === sequence.length - 1 || sequence[index + 1]?.type === "node";

					return (
						<Box key={`arc-${item.arc.arcId || index}`} sx={{ display: "flex", gap: 2 }}>
							{/* Colonna sinistra: icona + linea verticale */}
							<Box
								sx={{
									width: 60,
									position: "relative",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
								}}
							>
								{/* Linea verticale locale all'arco */}
								<Box
									sx={{
										position: "absolute",
										left: 30, // Centro della colonna (60px / 2 = 30px)
										top: isFirstArc ? 20 : -20, // Estende sopra se non è il primo
										bottom: isLastArc ? 20 : -20, // Estende sotto se non è l'ultimo
										width: 2,
										bgcolor: "grey.300",
										zIndex: 0,
									}}
								/>
								{/* Icona veicolo circolare */}
								<Box
									sx={{
										position: "relative",
										zIndex: 1,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										width: 40,
										height: 40,
										borderRadius: "50%",
										bgcolor: "grey.200",
									}}
								>
									<IconComponent fontSize="small" color="primary" />
								</Box>
							</Box>
							{/* Colonna destra: card con info */}
							<Box sx={{ flex: 1 }}>
								<ArcCard arc={item.arc} />
							</Box>
						</Box>
					);
				}
			})}
		</Stack>
	);
};

export default ArcTab;
